import * as React from "react";
import { Check, ChevronsUpDown, Search, Plus } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * SearchableSelectWithQuickAdd - An enhanced SearchableSelect that supports quick-add functionality
 * 
 * Props:
 * - options: Array of { value, label } objects
 * - value: Currently selected value
 * - onValueChange: Callback when selection changes
 * - placeholder: Placeholder text when nothing selected
 * - searchPlaceholder: Placeholder for search input
 * - emptyText: Text when no results found (not used when quickAdd is enabled)
 * - disabled: Whether the select is disabled
 * - className: Additional CSS classes
 * - quickAdd: Object with { enabled: true, label: "Add new place", onAdd: (searchTerm) => {} }
 */
function SearchableSelectWithQuickAdd(props) {
  const {
    options = [],
    value,
    onValueChange,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    emptyText = "No results found.",
    disabled = false,
    className,
    quickAdd = null, // { enabled: true, label: "Add new", onAdd: (searchTerm) => {} }
  } = props;
  const testId = props["data-testid"];

  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const selectedOption = options.find(function(opt) { return opt.value === value; });

  const filteredOptions = React.useMemo(function() {
    const validOptions = options.filter(function(opt) {
      return opt.value && opt.value.toString().trim() !== '';
    });
    
    if (!searchQuery) return validOptions;
    const query = searchQuery.toLowerCase();
    return validOptions.filter(function(opt) {
      return (opt.label && opt.label.toLowerCase().includes(query)) ||
        (opt.value && opt.value.toString().toLowerCase().includes(query));
    });
  }, [options, searchQuery]);

  const showQuickAdd = quickAdd?.enabled && searchQuery.trim() && filteredOptions.length === 0;

  const handleQuickAdd = () => {
    if (quickAdd?.onAdd) {
      quickAdd.onAdd(searchQuery.trim());
      setOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={function(e) { setSearchQuery(e.target.value); }}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-60 overflow-y-auto">
            {/* Show quick-add option when no results and quickAdd is enabled */}
            {showQuickAdd ? (
              <div className="p-2">
                <p className="text-sm text-gray-500 mb-2 px-2">No results found</p>
                <Button
                  variant="outline"
                  className="w-full justify-start text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={handleQuickAdd}
                  data-testid="quick-add-trigger"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {quickAdd.label ? `${quickAdd.label} "${searchQuery}"` : `Add "${searchQuery}"`}
                </Button>
              </div>
            ) : (
              <>
                {filteredOptions.length === 0 && !quickAdd?.enabled && (
                  <CommandEmpty>{emptyText}</CommandEmpty>
                )}
                <CommandGroup>
                  {filteredOptions.map(function(option) {
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={function() {
                          onValueChange(option.value === value ? "" : option.value);
                          setOpen(false);
                          setSearchQuery("");
                        }}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === option.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="truncate">{option.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
          
          {/* Always show quick-add button at bottom when enabled (even with results) */}
          {quickAdd?.enabled && !showQuickAdd && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-blue-600 hover:bg-blue-50"
                onClick={() => {
                  if (searchQuery.trim()) {
                    handleQuickAdd();
                  } else {
                    quickAdd.onAdd('');
                  }
                }}
                data-testid="quick-add-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                {quickAdd.buttonLabel || "Add new..."}
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

SearchableSelectWithQuickAdd.displayName = "SearchableSelectWithQuickAdd";

export { SearchableSelectWithQuickAdd };
export default SearchableSelectWithQuickAdd;
